import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkle, Spinner, CheckCircle, WarningCircle, ListChecks } from "@phosphor-icons/react";
import { useSpecSchemas } from "@/lib/specSchemas";

const CONF_CLS = { alta: "bg-emerald-50 text-emerald-700 border-emerald-200", media: "bg-[#7E57C2]/10 text-[#7E57C2] border-[#7E57C2]/30", baja: "bg-amber-50 text-amber-700 border-amber-200" };

// Completa con Alendra AI las fichas técnicas incompletas: genera sugerencias
// producto por producto, el usuario revisa/selecciona y se aplican solo a campos vacíos.
export default function BulkSpecAIDialog({ open, onOpenChange, products, onApplied }) {
  const schemas = useSpecSchemas();
  const [rows, setRows] = useState([]);      // {product, status, specs, confidence, notes, checked, applied}
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);

  const schemaData = schemas.data;
  const candidates = useMemo(() => products.filter((p) => {
    const pr = schemas.progress(p.category, p.specs);
    return pr.hasSchema && pr.total > 0 && !pr.complete;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [products, schemaData]);

  useEffect(() => {
    if (open) setRows(candidates.map((p) => ({ product: p, status: "pending", specs: {}, confidence: {}, notes: "", checked: false, applied: [] })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const labelFor = (p, key) => schemas.schemaFor(p.category).find((f) => f.key === key)?.label || key;

  const generate = async () => {
    setRunning(true);
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (r.status === "done" || r.status === "applied") continue;
      setRows((rs) => rs.map((x, j) => (j === i ? { ...x, status: "loading" } : x)));
      try {
        const { data } = await api.post(`/alendra/products/${r.product.id}/suggest-specs`);
        const n = Object.keys(data.specs || {}).length;
        setRows((rs) => rs.map((x, j) => (j === i ? { ...x, status: "done", specs: data.specs || {}, confidence: data.confidence || {}, notes: data.notes || "", checked: n > 0 } : x)));
      } catch (err) {
        setRows((rs) => rs.map((x, j) => (j === i ? { ...x, status: "error", notes: err?.response?.data?.detail || "Error" } : x)));
      }
    }
    setRunning(false);
  };

  const apply = async () => {
    const targets = rows.filter((r) => r.checked && r.status === "done" && Object.keys(r.specs).length > 0);
    if (targets.length === 0) return toast.info("No hay sugerencias seleccionadas.");
    setApplying(true);
    let ok = 0;
    for (const r of targets) {
      try {
        const { data } = await api.post(`/alendra/products/${r.product.id}/apply-specs`, { specs: r.specs, mode: "fill_empty" });
        ok += 1;
        setRows((rs) => rs.map((x) => (x.product.id === r.product.id ? { ...x, status: "applied", checked: false, applied: data.applied || [] } : x)));
      } catch (err) {
        toast.error(`${r.product.name}: ${err?.response?.data?.detail || "no se pudo aplicar"}`);
      }
    }
    setApplying(false);
    toast.success(`Ficha técnica actualizada en ${ok} producto(s)`);
    onApplied?.();
  };

  const done = rows.filter((r) => r.status === "done" || r.status === "applied" || r.status === "error").length;
  const selectable = rows.filter((r) => r.status === "done" && Object.keys(r.specs).length > 0);
  const toggleAll = (v) => setRows((rs) => rs.map((r) => (r.status === "done" && Object.keys(r.specs).length > 0 ? { ...r, checked: v } : r)));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!running && !applying) onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="bulk-spec-ai-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Sparkle size={20} weight="fill" className="text-[#002FA7]" /> Completar fichas técnicas con Alendra AI</DialogTitle>
          <DialogDescription>
            {candidates.length} producto(s) del listado actual tienen ficha incompleta. Alendra sugiere solo lo que puede deducir del nombre y las características; tú revisas y aplicas. Nunca sobrescribe campos ya llenos.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3 flex-wrap border-y border-zinc-100 py-2">
          <div className="text-xs text-zinc-600 flex items-center gap-3">
            {rows.length > 0 && (
              <span className="font-mono-data" data-testid="bulk-spec-progress">{done}/{rows.length} analizados</span>
            )}
            {selectable.length > 0 && (
              <>
                <button type="button" className="underline" onClick={() => toggleAll(true)} data-testid="bulk-spec-select-all">Seleccionar todas</button>
                <button type="button" className="underline" onClick={() => toggleAll(false)} data-testid="bulk-spec-select-none">Ninguna</button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={generate} disabled={running || applying || rows.length === 0 || done === rows.length}
              className="bg-gradient-to-br from-[#002FA7] to-[#003ed4] text-white gap-1" data-testid="bulk-spec-generate">
              {running ? <Spinner size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />} {running ? "Analizando…" : "Generar sugerencias"}
            </Button>
            <Button size="sm" onClick={apply} disabled={applying || running || rows.filter((r) => r.checked).length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="bulk-spec-apply">
              {applying ? <Spinner size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />} Aplicar seleccionadas ({rows.filter((r) => r.checked).length})
            </Button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 -mx-2 px-2">
          {rows.length === 0 ? (
            <div className="text-sm text-zinc-500 italic py-10 text-center" data-testid="bulk-spec-empty">Todas las fichas del listado están completas 🎉</div>
          ) : (
            <ul className="divide-y divide-zinc-100" data-testid="bulk-spec-rows">
              {rows.map((r) => {
                const keys = Object.keys(r.specs);
                return (
                  <li key={r.product.id} className="py-2.5 flex gap-3 items-start" data-testid={`bulk-spec-row-${r.product.id}`}>
                    <input type="checkbox" className="mt-1 h-4 w-4 accent-[#002FA7]" checked={r.checked} disabled={r.status !== "done" || keys.length === 0}
                      onChange={(e) => setRows((rs) => rs.map((x) => (x.product.id === r.product.id ? { ...x, checked: e.target.checked } : x)))}
                      data-testid={`bulk-spec-check-${r.product.id}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-zinc-900 truncate">{r.product.name}</span>
                        <span className="text-[10px] uppercase tracking-widest text-zinc-500">{r.product.category}</span>
                        {r.status === "loading" && <Spinner size={13} className="animate-spin text-[#002FA7]" />}
                        {r.status === "error" && <span className="text-[10px] text-[#E0000F] inline-flex items-center gap-1"><WarningCircle size={12} /> {r.notes}</span>}
                        {r.status === "applied" && <span className="text-[10px] text-emerald-700 inline-flex items-center gap-1 font-semibold" data-testid={`bulk-spec-applied-${r.product.id}`}><CheckCircle size={12} weight="fill" /> {r.applied.length} campo(s) aplicados</span>}
                        {r.status === "done" && keys.length === 0 && <span className="text-[10px] text-zinc-400 italic">sin datos suficientes para sugerir</span>}
                      </div>
                      {(r.status === "done" || r.status === "applied") && keys.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {keys.map((k) => (
                            <span key={k} className={`text-[11px] px-2 py-0.5 rounded border ${CONF_CLS[r.confidence[k]] || CONF_CLS.media}`} title={`confianza ${r.confidence[k] || "media"}`} data-testid={`bulk-spec-val-${r.product.id}-${k}`}>
                              <span className="text-zinc-500">{labelFor(r.product, k)}:</span> <b>{r.specs[k]}</b>
                            </span>
                          ))}
                        </div>
                      )}
                      {r.status === "done" && r.notes && <div className="text-[10px] text-zinc-500 mt-1 italic">{r.notes}</div>}
                    </div>
                    <div className="text-[10px] font-mono-data text-zinc-500 shrink-0 inline-flex items-center gap-1" title="Obligatorios de la ficha">
                      <ListChecks size={11} /> {schemas.progress(r.product.category, r.product.specs).filled}/{schemas.progress(r.product.category, r.product.specs).total}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
